import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, CircularProgress, Chip, Grid, useTheme, useMediaQuery, Stack,
  Button, Tooltip, IconButton, InputAdornment, Card, Menu, MenuItem, Popover,
  Checkbox, FormControlLabel, Divider, Badge, Select, Skeleton,
} from "@mui/material";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import WidgetsRoundedIcon from "@mui/icons-material/WidgetsRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import Sidebar from "../Sidebar/Sidebar";
import config from "../../config";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";

const rowsPerPage = 10;

const DATE_TYPE_OPTIONS = [
  { value: "date", label: "Specific Date" },
  { value: "lifetime", label: "Lifetime" },
  { value: "monthly", label: "Monthly" },
  { value: "weekly", label: "Weekly" },
];

const toLocalDateInputValue = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatNumber = (value) => {
  const number = Number(value);
  return Number.isNaN(number) ? "-" : number.toLocaleString("en-IN");
};

const formatDateLabel = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const formatPriceRange = (min, max) => {
  if (min === null || max === null) return null;
  if (min === max) return `₹${formatNumber(min)}`;
  return `₹${formatNumber(min)} - ₹${formatNumber(max)}`;
};

const formatBuyers = (buyers) => {
  if (!Array.isArray(buyers) || buyers.length === 0) return "";
  return buyers.map((b) => b?.buyerName).filter(Boolean).join(", ");
};

const emptySummary = {
  totalMandis: 0, totalVarieties: 0, totalHandQty: 0,
  totalCombineQty: 0, totalPurchaseQty: 0, totalBuyers: 0,
};

const parseQty = (rawStr, numberField) => {
  if (numberField !== undefined && numberField !== null && Number(numberField) > 0) {
    return { value: Number(numberField), rangeLabel: null };
  }

  const str = String(rawStr ?? "").trim();
  if (!str || str === "-") return { value: 0, rangeLabel: null };

  const parts = str
    .split("-")
    .map((p) => Number(String(p).replace(/[^\d.]/g, "")))
    .filter((p) => !Number.isNaN(p));

  if (parts.length === 0) return { value: 0, rangeLabel: null };
  if (parts.length === 1) return { value: parts[0], rangeLabel: null };

  return { value: (parts[0] + parts[1]) / 2, rangeLabel: `${parts[0]}-${parts[1]}` };
};

const normalizeRow = (item) => {
  let handPriceMin = null;
  let handPriceMax = null;
  if (item.handPrice !== undefined && item.handPrice !== null) {
    const handPrice = String(item.handPrice).trim();
    if (handPrice && handPrice !== "-") {
      const prices = handPrice.split("-")
        .map((p) => Number(String(p).replace(/[^\d.]/g, "")))
        .filter((p) => !Number.isNaN(p));
      if (prices.length === 1) { handPriceMin = prices[0]; handPriceMax = prices[0]; }
      else if (prices.length >= 2) { handPriceMin = prices[0]; handPriceMax = prices[1]; }
    }
  }

  let combinePrice = null;
  if (item.combinePrice !== undefined && item.combinePrice !== null && String(item.combinePrice).trim() !== "") {
    const priceText = String(item.combinePrice).trim();
    if (priceText !== "-") {
      const prices = priceText.split("-")
        .map((p) => Number(String(p).replace(/[^\d.]/g, "")))
        .filter((p) => !Number.isNaN(p));
      if (prices.length === 1) combinePrice = prices[0];
      else if (prices.length >= 2) combinePrice = `${prices[0]}-${prices[1]}`;
    }
  }

  const handQtyParsed = parseQty(item.handQty, item.handQtyNumber);
  const combineQtyParsed = parseQty(item.combineQty, item.combineQtyNumber);

  return {
    ...item,
    mandi: item.mandi || item.location || "-",
    variety: item.variety || "-",
    handQty: handQtyParsed.value,
    handQtyRange: handQtyParsed.rangeLabel,
    combineQty: combineQtyParsed.value,
    combineQtyRange: combineQtyParsed.rangeLabel,
    handPriceMin,
    handPriceMax,
    combinePrice,
    purchaseOffer: item.purchaseOffer && item.purchaseOffer !== "-" ? item.purchaseOffer : null,
    buyers: Array.isArray(item.buyers)
      ? item.buyers.map((b) => ({ buyerName: b?.buyerName || "-", purchaseQuantity: b?.purchaseQuantity ?? null }))
      : [],
    buyerCount: item.buyerCount !== undefined && item.buyerCount !== null
      ? Number(item.buyerCount) || 0
      : Array.isArray(item.buyers) ? item.buyers.length : 0,
    purchase: item.purchase !== undefined && item.purchase !== null ? Number(item.purchase) || 0 : 0,
    purchaseType: item.purchaseType || "",
    personName: item.personName || "",
    businessName: item.businessName || "",
    callDate: item.callDate || null,
    createdAt: item.createdAt || null,
    callTimeStamp: item.callTimeStamp || "",
  };
};

export default function MandiReportDetails() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const todayValue = useMemo(() => toLocalDateInputValue(new Date()), []);
  const [dateType, setDateType] = useState("date");
  const [selectedDate, setSelectedDate] = useState(todayValue);
  const [pendingDateType, setPendingDateType] = useState("date");
  const [pendingDate, setPendingDate] = useState(todayValue);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [exportAnchor, setExportAnchor] = useState(null);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [dateFilterAnchor, setDateFilterAnchor] = useState(null);
  const [selectedMandis, setSelectedMandis] = useState([]);
  const [selectedVarieties, setSelectedVarieties] = useState([]);
  const [summary, setSummary] = useState(emptySummary);
  const [availableDates, setAvailableDates] = useState([]);
  const [dateFileCounts, setDateFileCounts] = useState({});
  const [filterMandiOptions, setFilterMandiOptions] = useState([]);
  const [filterVarietyOptions, setFilterVarietyOptions] = useState([]);

  useEffect(() => {
    let ignore = false;

    const loadFilters = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const response = await axios.get(
          `${config.apiBaseUrl}/dashboard/market-report-dashboard/filters`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (ignore) return;

        const data = response?.data?.data;
        if (!data) return;

        const dates = Array.isArray(data.dates) ? data.dates : [];
        setAvailableDates(dates.map((d) => d?.date).filter(Boolean));
        setDateFileCounts(
          dates.reduce((acc, d) => {
            if (d?.date) acc[d.date] = Number(d.fileCount) || 0;
            return acc;
          }, {})
        );
        setFilterMandiOptions(Array.isArray(data.mandis) ? data.mandis : []);
        setFilterVarietyOptions(Array.isArray(data.varieties) ? data.varieties : []);
      } catch (error) {
        if (ignore) return;
        // console.error("Fetch report filters error:", error);
      }
    };

    loadFilters();
    return () => { ignore = true; };
  }, []);


  const FETCH_LIMIT = 5000;

  useEffect(() => {
    const controller = new AbortController();

    const timeoutId = setTimeout(async () => {
      try {
        setLoading(true);
        const token = sessionStorage.getItem("token");

        const params = { dateType, limit: FETCH_LIMIT };
        if (dateType === "date") params.date = selectedDate;
        if (selectedMandis.length) params.mandi = selectedMandis.join(",");
        if (selectedVarieties.length) params.variety = selectedVarieties.join(",");

        const response = await axios.get(`${config.apiBaseUrl}/dashboard/market-report-dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
          params,
          signal: controller.signal,
        });

        const responseData = response?.data;
        if (!responseData?.success) {
          setRows([]);
          setSummary(emptySummary);
          setPage(1);
          return;
        }

        const apiRows = Array.isArray(responseData?.data) ? responseData.data : [];
        const normalizedRows = apiRows.map(normalizeRow);
        setRows(normalizedRows);

        const apiSummary = responseData?.summary || {};
        const apiTotalHandQty = Number(apiSummary.totalHandQty) || 0;
        const apiTotalCombineQty = Number(apiSummary.totalCombineQty) || 0;
        const computedTotalHandQty = normalizedRows.reduce((sum, r) => sum + (r.handQty || 0), 0);
        const computedTotalCombineQty = normalizedRows.reduce((sum, r) => sum + (r.combineQty || 0), 0);

        setSummary({
          totalMandis: Number(apiSummary.totalMandis) || 0,
          totalVarieties: Number(apiSummary.totalVarieties) || 0,
          totalHandQty: apiTotalHandQty > 0 ? apiTotalHandQty : computedTotalHandQty,
          totalCombineQty: apiTotalCombineQty > 0 ? apiTotalCombineQty : computedTotalCombineQty,
          totalPurchaseQty: Number(apiSummary.totalPurchaseQty) || 0,
          totalBuyers: Number(apiSummary.totalBuyers) || 0,
        });

        setPage(1);
      } catch (error) {
        if (axios.isCancel?.(error) || error.name === "CanceledError" || error.name === "AbortError") return;
        // console.error("Dashboard analytics error:", error);
        setRows([]);
        setSummary(emptySummary);
        setPage(1);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [dateType, selectedDate, selectedMandis, selectedVarieties]);

  const filteredRows = useMemo(() => {
    if (!search) return rows;
    const term = search.toLowerCase();
    return rows.filter((row) => String(row.variety || "").toLowerCase().includes(term));
  }, [rows, search]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const pagedRows = filteredRows.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);
  useEffect(() => { setPage(1); }, [search]); 

  const activeFilterCount = selectedMandis.length + selectedVarieties.length;

  const clearFilters = () => {
    setSelectedMandis([]);
    setSelectedVarieties([]);
    setPage(1);
  };

  const toggleMandi = (mandi) => {
    setSelectedMandis((prev) => prev.includes(mandi) ? prev.filter((m) => m !== mandi) : [...prev, mandi]);
    setPage(1);
  };

  const toggleVariety = (variety) => {
    setSelectedVarieties((prev) => prev.includes(variety) ? prev.filter((v) => v !== variety) : [...prev, variety]);
    setPage(1);
  };

  const handlePendingDateTypeChange = (value) => {
    setPendingDateType(value);
    if (value !== "date") setPendingDate(todayValue);
  };

  const handlePendingDateChange = (newValue) => {
    if (!newValue) return;
    const value = newValue.format("YYYY-MM-DD");
    if (value > todayValue) return;
    setPendingDate(value);
  };

  const openDateFilter = (e) => {
    setPendingDateType(dateType);
    setPendingDate(selectedDate);
    setDateFilterAnchor(e.currentTarget);
  };

  const applyDateFilter = () => {
    setDateType(pendingDateType);
    setSelectedDate(pendingDate);
    setSelectedMandis([]);
    setSelectedVarieties([]);
    setPage(1);
    setDateFilterAnchor(null);
  };

  const dateFilterLabel = dateType === "date"
    ? formatDateLabel(selectedDate) || "Select date"
    : DATE_TYPE_OPTIONS.find((o) => o.value === dateType)?.label || "Specific Date";

  const { totalMandis, totalVarieties, totalHandQty, totalCombineQty } = summary;

  const displayQty = (row, field) => {
    const rangeField = field === "hand" ? "handQtyRange" : "combineQtyRange";
    const qtyField = field === "hand" ? "handQty" : "combineQty";
    return row[rangeField] || formatNumber(row[qtyField]);
  };

  const handleCopyTable = async () => {
    if (!filteredRows.length) return;
    const header = ["Mandi", "Variety", "Hand Qty", "Hand Price", "Combine Qty", "Combine Price", "Purchase Offer", "Buyers"].join("\t");
    const body = filteredRows.map((r) => [
      r.mandi,
      r.variety,
      displayQty(r, "hand"),
      formatPriceRange(r.handPriceMin, r.handPriceMax) || "-",
      displayQty(r, "combine"),
      r.combinePrice !== null ? `₹${r.combinePrice}` : "-",
      r.purchaseOffer || "-",
      formatBuyers(r.buyers) || "-",
    ].join("\t")).join("\n");

    try {
      await navigator.clipboard.writeText(`${header}\n${body}`);
    } catch (err) {
      console.error("Copy table error:", err);
    }
  };

  const handleExport = (format) => {
    setExportAnchor(null);
    if (format === "csv" && filteredRows.length) {
      const header = ["Mandi", "Variety", "Hand Qty", "Hand Price Min", "Hand Price Max", "Combine Qty", "Combine Price", "Purchase Offer", "Buyers"].join(",");
      const body = filteredRows.map((r) => [
        `"${String(r.mandi ?? "").replace(/"/g, '""')}"`,
        `"${String(r.variety ?? "").replace(/"/g, '""')}"`,
        `"${displayQty(r, "hand")}"`,
        r.handPriceMin ?? "",
        r.handPriceMax ?? "",
        `"${displayQty(r, "combine")}"`,
        `"${String(r.combinePrice ?? "").replace(/"/g, '""')}"`,
        `"${String(r.purchaseOffer ?? "").replace(/"/g, '""')}"`,
        `"${formatBuyers(r.buyers).replace(/"/g, '""')}"`,
      ].join(",")).join("\n");

      const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `mandi-report-${dateType}${dateType === "date" ? `-${selectedDate}` : ""}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const statCards = [
    { label: "Total Mandis", value: totalMandis, sub: "Active Mandis", icon: StorefrontRoundedIcon, color: "#16A34A", bg: "rgba(22,163,74,0.10)" },
    { label: "Total Varieties", value: totalVarieties, sub: "Varieties Reported", icon: GridViewRoundedIcon, color: "#2563EB", bg: "rgba(37,99,235,0.10)" },
    { label: "Total Hand Qty", value: formatNumber(totalHandQty), sub: "Total Quantity", icon: Inventory2RoundedIcon, color: "#7C3AED", bg: "rgba(124,58,237,0.10)" },
    { label: "Total Combine Qty", value: formatNumber(totalCombineQty), sub: "Total Quantity", icon: LocalShippingRoundedIcon, color: "#D97706", bg: "rgba(217,119,6,0.10)" },
  ];

  const InfoHeader = ({ label }) => (
    <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>{label}</Typography>
  );

  const MOBILE_COLUMN_COUNT = 5; 

  const mobileHeadCellSx = {
    px: 1,
    py: 1,
    fontSize: "0.64rem",
    fontWeight: 700,
    letterSpacing: "0.03em",
    textTransform: "uppercase",
    color: "rgba(15,23,42,0.55)",
    background: "#F8FAFC",
    borderBottom: "2px solid rgba(15,23,42,0.08)",
    borderRight: "1px solid rgba(15,23,42,0.10)",
    whiteSpace: "nowrap",
  };
  const mobileBodyCellSx = {
    px: 1,
    py: 1,
    fontSize: "0.75rem",
    borderBottom: "1px solid rgba(15,23,42,0.06)",
    borderRight: "1px solid rgba(15,23,42,0.08)",
  };
  const stickyColSx = {
    position: "sticky",
    left: 0,
    zIndex: 1,
    boxShadow: "2px 0 4px -2px rgba(15,23,42,0.18)",
  };

  const QtyPriceCell = ({ qty, price, icon: Icon, iconColor }) => (
    <Stack spacing={0.3}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        {Icon && <Icon sx={{ fontSize: 14, color: iconColor, flexShrink: 0 }} />}
        <Typography sx={{ fontWeight: 700, fontSize: "0.78rem" }}>{qty}</Typography>
      </Stack>
      <Typography noWrap sx={{ fontSize: "0.62rem", color: "rgba(15,23,42,0.5)" }}>
        {price}
      </Typography>
    </Stack>
  );

  const FilterCalendarDay = (props) => {
    const { day, outsideCurrentMonth, selected, ...other } = props;
    const dateString = day.format("YYYY-MM-DD");
    const fileCount = dateFileCounts[dateString];
    const hasReport = Boolean(fileCount);

    const pickersDay = (
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        selected={selected}
        sx={{
          ...(hasReport && !selected && !outsideCurrentMonth && {
            color: "#16A34A", fontWeight: 800,
            "&:hover": { backgroundColor: "rgba(22,163,74,0.10)" },
          }),
          ...(selected && !outsideCurrentMonth && {
            backgroundColor: "#2563EB !important", color: "#FFFFFF !important", fontWeight: 800,
            border: "2px solid #0F172A", boxSizing: "border-box",
            "&:hover": { backgroundColor: "#1D4ED8 !important" },
            "&:focus": { backgroundColor: "#2563EB !important" },
          }),
        }}
      />
    );

    if (hasReport && !outsideCurrentMonth) {
      return (
        <Tooltip title={`${fileCount} report${fileCount === 1 ? "" : "s"}`}>
          {pickersDay}
        </Tooltip>
      );
    }
    return pickersDay;
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", overflowX: "hidden" }}>
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 2.5, md: 3 }, background: "#F4F6F8", overflowX: "hidden", width: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" rowGap={2} mb={3}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {isTablet && (
              <IconButton onClick={() => setMobileOpen(true)} sx={{ border: "1px solid #E0E0E0", borderRadius: "12px" }}>
                <MenuRoundedIcon />
              </IconButton>
            )}
            <Box sx={{ width: 52, height: 52, flexShrink: 0, borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", background: "linear-gradient(135deg,#16A34A 0%,#059669 100%)" }}>
              <StorefrontRoundedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant={isMobile ? "h6" : "h4"} fontWeight={800}>Mandi Report Details</Typography>
              <Typography sx={{ color: "rgba(15,23,42,0.55)", fontSize: "0.85rem" }}>View variety-wise market report</Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              onClick={openDateFilter}
              startIcon={<CalendarMonthRoundedIcon sx={{ fontSize: 18 }} />}
              endIcon={<ExpandMoreRoundedIcon sx={{ fontSize: 18 }} />}
              sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 600, borderColor: "rgba(15,23,42,0.15)", color: "#0F172A", whiteSpace: "nowrap" }}
            >
              {dateFilterLabel}
            </Button>

            <Popover
              open={Boolean(dateFilterAnchor)}
              anchorEl={dateFilterAnchor}
              onClose={() => setDateFilterAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Box sx={{ p: 2.25, width: 260 }}>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>Report Period</Typography>
                <Select
                  size="small"
                  fullWidth
                  value={pendingDateType}
                  onChange={(e) => handlePendingDateTypeChange(e.target.value)}
                  sx={{ borderRadius: "10px", mb: 1.25 }}
                >
                  {DATE_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>

                {pendingDateType === "date" && (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={pendingDate ? dayjs(pendingDate) : null}
                      onChange={handlePendingDateChange}
                      maxDate={dayjs(todayValue)}
                      slots={{ day: FilterCalendarDay }}
                      slotProps={{
                        textField: { size: "small", fullWidth: true, sx: { mb: 1.25 } },
                        field: { clearable: false },
                      }}
                    />
                  </LocalizationProvider>
                )}

                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="contained"
                    onClick={applyDateFilter}
                    sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none", background: "#16A34A", "&:hover": { background: "#15803D" } }}
                  >
                    Apply
                  </Button>
                </Stack>
              </Box>
            </Popover>

            <Button
              variant="contained"
              onClick={(e) => setExportAnchor(e.currentTarget)}
              startIcon={<FileDownloadRoundedIcon sx={{ fontSize: 18 }} />}
              endIcon={<ExpandMoreRoundedIcon sx={{ fontSize: 18 }} />}
              sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 600, boxShadow: "none", background: "#0F172A", whiteSpace: "nowrap", "&:hover": { background: "#1E293B" } }}
            >
              Export
            </Button>
            <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
              <MenuItem onClick={() => handleExport("csv")} disabled={!rows.length}>Export as CSV</MenuItem>
              {/* <MenuItem onClick={() => handleExport("pdf")} disabled>Export as PDF (coming soon)</MenuItem> */}
            </Menu>
          </Stack>
        </Stack>

        <Grid container spacing={2} mb={3} sx={{ alignItems: "stretch" }}>
          {statCards.map((card) => (
            <Grid item xs={12} sm={6} md={3} key={card.label} sx={{ display: "flex" }}>
              <Card
                elevation={0}
                sx={{
                  p: 2.25, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.06)",
                  boxShadow: "0 6px 18px rgba(15,23,42,0.04)", display: "flex", alignItems: "center",
                  gap: 1.75, width: "100%", height: 104, boxSizing: "border-box",
                }}
              >
                {loading ? (
                  <>
                    <Skeleton variant="circular" width={56} height={56} sx={{ flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Skeleton variant="text" width="60%" height={18} />
                      <Skeleton variant="text" width="45%" height={30} />
                      <Skeleton variant="text" width="50%" height={16} />
                    </Box>
                  </>
                ) : (
                  <>
                    <Box sx={{ width: 56, height: 56, flexShrink: 0, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: card.bg, color: card.color }}>
                      <card.icon sx={{ fontSize: 26 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: "0.85rem", color: "rgba(15,23,42,0.55)" }}>{card.label}</Typography>
                      <Typography noWrap sx={{ fontSize: "1.6rem", fontWeight: 800 }}>{card.value}</Typography>
                      <Typography noWrap sx={{ fontSize: "0.75rem", fontWeight: 600, color: card.color }}>{card.sub}</Typography>
                    </Box>
                  </>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card elevation={0} sx={{ borderRadius: "16px", p: { xs: 1.75, sm: 2.25 }, border: "1px solid rgba(15,23,42,0.06)", boxShadow: "0 6px 18px rgba(15,23,42,0.04)" }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5} mb={2}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <ListAltRoundedIcon sx={{ color: "#0F172A" }} />
              <Typography sx={{ fontWeight: 700, fontSize: "1.05rem" }}>Market Report Table</Typography>
              {loading && <CircularProgress size={16} />}
            </Stack>

            <Stack direction="row" spacing={1.25} flexWrap="wrap" rowGap={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
              <TextField
                size="small"
                placeholder="Search variety..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={!rows.length}
                InputProps={{ startAdornment: (
                  <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18, color: "rgba(15,23,42,0.4)" }} /></InputAdornment>
                ) }}
                sx={{ minWidth: { xs: 0, sm: 220 }, flexGrow: { xs: 1, sm: 0 }, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />

              <Badge color="error" variant="dot" invisible={activeFilterCount === 0} sx={{ "& .MuiBadge-dot": { top: 6, right: 6 } }}>
                <Button
                  variant="outlined"
                  disabled={!filterMandiOptions.length && !filterVarietyOptions.length}
                  onClick={(e) => setFilterAnchor(e.currentTarget)}
                  startIcon={<FilterListRoundedIcon sx={{ fontSize: 18 }} />}
                  sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, borderColor: activeFilterCount ? "#16A34A" : "rgba(15,23,42,0.15)", color: activeFilterCount ? "#16A34A" : "#0F172A", whiteSpace: "nowrap" }}
                >
                  Filters{activeFilterCount ? ` (${activeFilterCount})` : ""}
                </Button>
              </Badge>

              <Popover
                open={Boolean(filterAnchor)}
                anchorEl={filterAnchor}
                onClose={() => setFilterAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <Box sx={{ p: 2.25, width: 260 }}>
                  <Typography sx={{ fontWeight: 700, mb: 1 }}>Mandi</Typography>
                  <Stack spacing={0.25} sx={{ mb: 1.5, maxHeight: 180, overflowY: "auto" }}>
                    {filterMandiOptions.length > 0 ? filterMandiOptions.map((mandi) => (
                      <FormControlLabel
                        key={mandi}
                        control={<Checkbox size="small" checked={selectedMandis.includes(mandi)} onChange={() => toggleMandi(mandi)} />}
                        label={mandi}
                      />
                    )) : (
                      <Typography sx={{ fontSize: "0.8rem", color: "rgba(15,23,42,0.5)" }}>No mandis loaded yet</Typography>
                    )}
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  <Typography sx={{ fontWeight: 700, mb: 1 }}>Variety</Typography>
                  <Stack spacing={0.25} sx={{ mb: 1.5, maxHeight: 180, overflowY: "auto" }}>
                    {filterVarietyOptions.length > 0 ? filterVarietyOptions.map((variety) => (
                      <FormControlLabel
                        key={variety}
                        control={<Checkbox size="small" checked={selectedVarieties.includes(variety)} onChange={() => toggleVariety(variety)} />}
                        label={variety}
                      />
                    )) : (
                      <Typography sx={{ fontSize: "0.8rem", color: "rgba(15,23,42,0.5)" }}>No varieties loaded yet</Typography>
                    )}
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Button
                      size="small"
                      onClick={clearFilters}
                      disabled={!activeFilterCount}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      Clear
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => setFilterAnchor(null)}
                      sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none", background: "#16A34A", "&:hover": { background: "#15803D" } }}
                    >
                      Done
                    </Button>
                  </Stack>
                </Box>
              </Popover>

              <Tooltip title="Copy table">
                <IconButton onClick={handleCopyTable} disabled={!rows.length} sx={{ border: "1px solid rgba(15,23,42,0.15)", borderRadius: "10px" }}>
                  <ContentCopyRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {loading ? (
            !isTablet ? (
              <Paper elevation={0} variant="outlined" sx={{ overflow: "auto", borderRadius: "12px", border: "1px solid rgba(15,23,42,0.10)" }}>
                <Table sx={{ "& th, & td": { borderBottom: "1px solid rgba(15,23,42,0.10)" } }}>
                  <TableHead>
                    <TableRow sx={{ background: "rgba(15,23,42,0.02)" }}>
                      {["#", "Mandi", "Variety", "Hand Qty", "Hand Price", "Combine Qty", "Combine Price", "Purchase Offer", "Buyers"].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.from({ length: rowsPerPage }, (_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 9 }, (_, c) => (
                          <TableCell key={c}><Skeleton variant="text" width={c === 0 ? 20 : "80%"} height={22} /></TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            ) : (
              <Paper elevation={0} variant="outlined" sx={{ overflow: "auto", borderRadius: "12px", border: "1px solid rgba(15,23,42,0.10)" }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...mobileHeadCellSx, ...stickyColSx, minWidth: 92 }}>Mandi</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 80 }}>Variety</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 100 }}>Hand</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 100 }}>Combine</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 130, borderRight: "none" }}>Buyers</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.from({ length: 6 }, (_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: MOBILE_COLUMN_COUNT }, (_, c) => (
                          <TableCell key={c} sx={mobileBodyCellSx}>
                            <Skeleton variant="text" width="70%" height={18} />
                            {(c === 2 || c === 3) && <Skeleton variant="text" width="50%" height={14} />}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )
          ) : rows.length === 0 ? (
            <Box sx={{ minHeight: 220, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", px: 2 }}>
              <Box sx={{ width: 64, height: 64, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(22,163,74,0.08)", color: "#16A34A", mb: 1.5 }}>
                <ListAltRoundedIcon sx={{ fontSize: 30 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>No market report available</Typography>
              <Typography sx={{ color: "rgba(15,23,42,0.5)", fontSize: "0.85rem", mt: 0.5 }}>
                No report was found for {dateFilterLabel}.
              </Typography>
            </Box>
          ) : !isTablet ? (
            <Paper elevation={0} variant="outlined" sx={{ overflow: "auto", borderRadius: "12px", border: "1px solid rgba(15,23,42,0.10)" }}>
              <Table sx={{
                "& th, & td": { borderBottom: "1px solid rgba(15,23,42,0.10)", borderRight: "1px solid rgba(15,23,42,0.10)" },
                "& th:last-of-type, & td:last-of-type": { borderRight: "none" },
                "& tr:last-of-type td": { borderBottom: "none" },
              }}>
                <TableHead>
                  <TableRow sx={{ background: "rgba(15,23,42,0.02)" }}>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Mandi</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Variety</TableCell>
                    <TableCell><InfoHeader label="Hand Qty" /></TableCell>
                    <TableCell><InfoHeader label="Hand Price" /></TableCell>
                    <TableCell><InfoHeader label="Combine Qty" /></TableCell>
                    <TableCell><InfoHeader label="Combine Price" /></TableCell>
                    <TableCell><InfoHeader label="Purchase Offer" /></TableCell>
                    <TableCell><InfoHeader label="Buyers" /></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.length > 0 ? pagedRows.map((row, idx) => (
                    <TableRow key={`${row.mandi}-${row.variety}-${idx}`} hover>
                      <TableCell sx={{ color: "rgba(15,23,42,0.55)" }}>{(page - 1) * rowsPerPage + idx + 1}</TableCell>
                      <TableCell>
                        <Chip icon={<StorefrontRoundedIcon sx={{ fontSize: 16 }} />} label={row.mandi || "-"} size="small" sx={{ background: "rgba(22,163,74,0.10)", color: "#166534", fontWeight: 600 }} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{row.variety || "-"}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Inventory2RoundedIcon sx={{ fontSize: 16, color: "#16A34A" }} />
                          <Typography sx={{ fontWeight: 600 }}>{displayQty(row, "hand")}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {formatPriceRange(row.handPriceMin, row.handPriceMax) ? (
                          <Chip label={formatPriceRange(row.handPriceMin, row.handPriceMax)} size="small" sx={{ background: "rgba(22,163,74,0.10)", color: "#166534", fontWeight: 600 }} />
                        ) : (
                          <Typography sx={{ color: "rgba(15,23,42,0.35)" }}>-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <WidgetsRoundedIcon sx={{ fontSize: 16, color: "#7C3AED" }} />
                          <Typography sx={{ fontWeight: 600 }}>{displayQty(row, "combine")}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip label={row.combinePrice !== null ? `₹${row.combinePrice}` : "₹ —"} size="small" sx={{ background: "rgba(124,58,237,0.12)", color: "#5B21B6", fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.purchaseOffer || "—"}
                          size="small"
                          sx={{ background: "rgba(217,119,6,0.14)", color: "#92400E", fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 200, maxWidth: 260 }}>
                        {row.buyers && row.buyers.length > 0 ? (
                          <Tooltip title={formatBuyers(row.buyers)} arrow>
                            <Typography noWrap sx={{ fontSize: "0.85rem", color: "#0F172A" }}>
                              {formatBuyers(row.buyers)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography sx={{ color: "rgba(15,23,42,0.35)" }}>—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ textAlign: "center", py: 5 }}>
                        <Typography sx={{ color: "rgba(15,23,42,0.5)" }}>No matching varieties found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <>
              {pagedRows.length > 0 && (
                <Typography sx={{ fontSize: "0.68rem", color: "rgba(15,23,42,0.45)", mb: 0.75, ml: 0.25 }}>
                  Swipe left for more →
                </Typography>
              )}
              <Paper elevation={0} variant="outlined" sx={{ overflow: "auto", borderRadius: "12px", border: "1px solid rgba(15,23,42,0.10)", WebkitOverflowScrolling: "touch" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...mobileHeadCellSx, ...stickyColSx, minWidth: 92 }}>Mandi</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 80 }}>Variety</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 100 }}>Hand</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 100 }}>Combine</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 140, borderRight: "none" }}>Buyers</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedRows.length > 0 ? pagedRows.map((row, idx) => {
                      const zebraSx = idx % 2 === 1 ? { background: "rgba(15,23,42,0.015)" } : {};
                      const stickyBg = idx % 2 === 1 ? "#FBFCFD" : "#fff";
                      const handPrice = formatPriceRange(row.handPriceMin, row.handPriceMax) || "-";
                      const combinePrice = row.combinePrice !== null ? `₹${row.combinePrice}` : "-";

                      return (
                        <TableRow key={`${row.mandi}-${row.variety}-${idx}`} sx={zebraSx}>
                          <TableCell sx={{ ...mobileBodyCellSx, ...stickyColSx, background: stickyBg, fontWeight: 700 }}>
                            {row.mandi || "-"}
                          </TableCell>
                          <TableCell sx={mobileBodyCellSx}>{row.variety || "-"}</TableCell>
                          <TableCell sx={mobileBodyCellSx}>
                            <QtyPriceCell qty={displayQty(row, "hand")} price={handPrice} icon={Inventory2RoundedIcon} iconColor="#16A34A" />
                          </TableCell>
                          <TableCell sx={mobileBodyCellSx}>
                            <QtyPriceCell qty={displayQty(row, "combine")} price={combinePrice} icon={WidgetsRoundedIcon} iconColor="#7C3AED" />
                          </TableCell>
                          <TableCell sx={{ ...mobileBodyCellSx, borderRight: "none" }}>
                            <Stack spacing={0.4}>
                              {row.buyers && row.buyers.length > 0 ? (
                                <Tooltip title={formatBuyers(row.buyers)} arrow>
                                  <Typography noWrap sx={{ fontSize: "0.72rem", maxWidth: 130 }}>
                                    {formatBuyers(row.buyers)}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography sx={{ fontSize: "0.72rem", color: "rgba(15,23,42,0.35)" }}>—</Typography>
                              )}
                              {row.purchaseOffer && (
                                <Chip
                                  label={row.purchaseOffer}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: "0.58rem",
                                    alignSelf: "flex-start",
                                    background: "rgba(217,119,6,0.14)",
                                    color: "#92400E",
                                    fontWeight: 700,
                                    "& .MuiChip-label": { px: 0.6 },
                                  }}
                                />
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    }) : (
                      <TableRow>
                        <TableCell colSpan={MOBILE_COLUMN_COUNT} sx={{ textAlign: "center", py: 5, borderRight: "none" }}>
                          <Typography sx={{ color: "rgba(15,23,42,0.5)" }}>No matching varieties found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </>
          )}

          {filteredRows.length > 0 && (
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" rowGap={1.5} mt={2}>
              <Typography sx={{ fontSize: "0.8rem", color: "rgba(15,23,42,0.55)" }}>
                Showing {(page - 1) * rowsPerPage + 1} to {Math.min(page * rowsPerPage, filteredRows.length)} of {filteredRows.length} entries
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton size="small" disabled={page === 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} sx={{ border: "1px solid rgba(15,23,42,0.15)", borderRadius: "8px" }}>
                  <ChevronLeftRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>

                {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
                  <Box
                    key={p}
                    onClick={() => !loading && setPage(p)}
                    sx={{
                      width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center",
                      borderRadius: "8px", cursor: loading ? "default" : "pointer", fontWeight: 600, fontSize: "0.85rem",
                      border: p === page ? "1px solid #16A34A" : "1px solid transparent",
                      color: p === page ? "#16A34A" : "#0F172A",
                      background: p === page ? "rgba(22,163,74,0.08)" : "transparent",
                    }}
                  >
                    {p}
                  </Box>
                ))}

                <IconButton size="small" disabled={page === pageCount || loading} onClick={() => setPage((p) => Math.min(pageCount, p + 1))} sx={{ border: "1px solid rgba(15,23,42,0.15)", borderRadius: "8px" }}>
                  <ChevronRightRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </Stack>
          )}
        </Card>
      </Box>
    </Box>
  );
}